import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Abrechnungsquellen } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useFormularZustand, useBestaetigung }
  from "@kundeninfo/ui";
import { Chip, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "./meHuellen.js";
import "./AbrechnungsquellenKasten.css";

/**
 * DER KASTEN „ABRECHNUNGSQUELLEN" am Auftrag (Spec „Abrechnungsquellen", 29.09.2026,
 * Entscheid 6: direkt unter „Zahlungsvereinbarung und Fälligkeiten").
 *
 * Je Quelle des Bedarfs (oder mit Verknüpfung) eine Zeile: Quelle, Zustand als Chip
 * (FEHLT rot, ganz oben — die Reihenfolge liefert der Server, Hausregel „Schlechtestes
 * oben"), die Verknüpfung und ihre Herkunft. „Verknüpfen" bzw. „Ändern" öffnet ein
 * STRIKTES Modal mit den Kandidaten des Kunden; ohne Kandidaten sagt es, wo sie gepflegt
 * werden. Ein „Lösen" bietet die Oberfläche am AKTIVEN Auftrag nicht an (Ruling
 * R-KEIN-LOESEN-IM-WEB — dort ersetzt „Ändern" die Verknüpfung, und ein Lösen erzeugte bloss
 * eine Störung).
 *
 * Fortgeschrieben 29.09.2026 (Entscheid Dirk, Punkt 7): An einem ABGESCHLOSSENEN oder
 * STORNIERTEN Auftrag bekommt ein ADMIN (`istAdmin`) je verknüpfter Quelle den Knopf
 * „Lösen" — mit Rückfrage (Bestätigungsfrage `useBestaetigung`, wie „Entfernen" in
 * Anhaenge.tsx),
 * denn es gibt kein Zurück: Setzen ist dort gesperrt. Anlass: Ein Portal-Mandant oder
 * Aircall-Account, der je an einem solchen Auftrag hing, liess sich sonst nie löschen.
 *
 * Der Kasten lädt NEU, wenn sich die Auftragsversion ändert (ein gespeicherter Zahlplan
 * kann Bedarf schaffen oder nehmen). Beim Nachladen bleibt die alte Sicht stehen, bis die
 * neue da ist — kein Zusammenfallen, keine verlorene Scrollposition (Hausregel).
 *
 * Jede geladene oder geschriebene Sicht geht an `onSicht`; nur die Antwort auf das EIGENE
 * erfolgreiche Speichern trägt `{ ausSchreiben: true }` (Fixrunde A10-1, Controller-Ruling):
 * Allein ihre Version darf der Rahmen in seinen Auftrag stempeln. Eine gelesene oder aus
 * einem Konflikt stammende Sicht mit fremder Version heisst „jemand anderes hat geändert" —
 * dann lädt der Rahmen neu, statt veraltete Felder mit der neuen Version zu versehen.
 * Der Rahmen führt daraus die
 * Störungszeile, den Listen-Chip und die Auftragsversion nach.
 *
 * Eigener CSS-Block `kaq` — mit keiner Ansicht geteilt.
 */

export type AbrechnungsquellenClient =
  Pick<ApiClient, "getAbrechnungsquellen" | "setzeAbrechnungsquelle" | "loeseAbrechnungsquelle">;

type Eintrag = Abrechnungsquellen["quellen"][number];
type Quelle = Eintrag["source"];
type Verknuepfung = NonNullable<Eintrag["verknuepfung"]>;

const QUELLE_WORT: Readonly<Record<Quelle, string>> = { MONITOR: "Monitor", AIRCALL: "Aircall" };
const OBJEKT_WORT: Readonly<Record<Quelle, string>> = {
  MONITOR: "Portal-Mandant", AIRCALL: "Aircall-Account",
};
/** Wo die Kandidaten gepflegt werden (Spec: „ohne Kandidaten sagt das Modal, wo sie
 *  gepflegt werden"). */
const PFLEGEORT: Readonly<Record<Quelle, string>> = {
  MONITOR: "Portal-Mandanten werden in der Kundenakte unter Stammdaten › Portal-Mandanten "
    + "zugeordnet.",
  AIRCALL: "Der Aircall-Account wird in der Kundenakte unter Stammdaten › Aircall-Account "
    + "gepflegt.",
};
const ZUSTAND_TEXT: Readonly<Record<Eintrag["zustand"], string>> = {
  FEHLT: "fehlt", VERKNUEPFT: "verknüpft", OHNE_BEDARF: "ohne Bedarf",
};
/** Zustand → Status-Art des Chips (me-chip `status-art`, P12 W2). */
const ZUSTAND_ART: Readonly<Record<Eintrag["zustand"], "fehler" | "ok" | "aus">> = {
  FEHLT: "fehler", VERKNUEPFT: "ok", OHNE_BEDARF: "aus",
};

/** Ruling R-STOERUNG: gestört heisst aktiv UND mindestens eine Quelle FEHLT. */
export function hatQuellenStoerung(sicht: Abrechnungsquellen): boolean {
  return sicht.status === "ACTIVE" && sicht.quellen.some((q) => q.zustand === "FEHLT");
}

/** Der Satz der Störungszeile — „Abrechnungsquelle fehlt: Monitor — Portal-Mandant
 *  verknüpfen". Ohne geladene Sicht (nur das Listenfeld ist bekannt) ein allgemeiner Satz. */
export function stoerungsText(sicht: Abrechnungsquellen | null): string {
  if (sicht === null) {
    return "Abrechnungsquelle fehlt — bitte im Kasten „Abrechnungsquellen“ verknüpfen.";
  }
  const teile = sicht.quellen.filter((q) => q.zustand === "FEHLT")
    .map((q) => `${QUELLE_WORT[q.source]} — ${OBJEKT_WORT[q.source]} verknüpfen`);
  return `Abrechnungsquelle fehlt: ${teile.join("; ")}`;
}

function herkunft(v: Verknuepfung): string {
  if (v.linkedBy === "AUTO") return "automatisch";
  return v.gewaehltVon === null ? "von Hand gewählt" : `gewählt von ${v.gewaehltVon}`;
}

export type AbrechnungsquellenKastenProps = {
  client: AbrechnungsquellenClient;
  customerId: string;
  orderId: string;
  /** Die Auftragsversion, die der Rahmen kennt — ändert sie sich, lädt der Kasten neu. */
  version: number;
  /** Präfix der Kennungen; der Container heisst `${idPraefix}-kasten` (Sprungziel). */
  idPraefix: string;
  /** `herkunft.ausSchreiben` nur nach dem eigenen erfolgreichen PUT bzw. DELETE (siehe
   *  Kopfkommentar). */
  onSicht: (sicht: Abrechnungsquellen, herkunft?: { ausSchreiben: true }) => void;
  /** Darf die Sitzung an nicht aktiven Aufträgen lösen (Rolle ADMIN, Entscheid 29.09.2026)?
   *  Weglassbar — dann nicht; den echten Riegel trägt ohnehin die Route. */
  istAdmin?: boolean | undefined;
};

type Dialog = { source: Quelle; wahl: string | null; vorher: string | null };

export function AbrechnungsquellenKasten(p: AbrechnungsquellenKastenProps): ReactElement {
  const { client, customerId, orderId, version } = p;
  const [sicht, setSicht] = useState<Abrechnungsquellen | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  /** Der mitgelieferte Serverstand der letzten 409 (`conflict.current`) — „Neu laden“
   *  übernimmt ihn, statt dasselbe noch einmal zu holen. */
  const konflikt = useRef<Abrechnungsquellen | null>(null);
  /** Läuft gerade ein Lösen? Sperrt alle Knöpfe des Kastens. */
  const [loest, setLoest] = useState(false);
  /** Die Meldungen des Lösens — AUSSERHALB eines Modals, also über der Liste (Hausregel
   *  „Fehlermeldungen stehen vor Listen"). */
  const [loesenMeldung, setLoesenMeldung] = useState<string[] | null>(null);
  /** Riegel gegen späte Antworten (Muster KundePortalMandanten). */
  const lauf = useRef(0);
  /** `onSicht` in einer Ref, damit ein neuer Rückruf des Rahmens keinen Abruf auslöst. */
  const onSicht = useRef(p.onSicht);
  useEffect(() => { onSicht.current = p.onSicht; });

  const getAbrechnungsquellen = client.getAbrechnungsquellen;

  useEffect(() => {
    const meiner = ++lauf.current;
    setLadeFehler(null);
    getAbrechnungsquellen(customerId, orderId)
      .then((s) => {
        if (lauf.current !== meiner) return;
        setSicht(s);
        onSicht.current(s);
      })
      .catch((e: unknown) => {
        if (lauf.current !== meiner) return;
        setLadeFehler(e instanceof ApiError ? e.message
          : "Die Abrechnungsquellen konnten nicht geladen werden.");
      });
    return () => { lauf.current += 1; };
  }, [getAbrechnungsquellen, customerId, orderId, version]);

  const aktiv = sicht?.status === "ACTIVE";
  /** Entscheid 29.09.2026: Lösen nur für ADMIN und nur am NICHT aktiven Auftrag. */
  const darfLoesen = p.istAdmin === true && sicht !== null && !aktiv;
  /** Der Eintrag, den das offene Modal zeigt — immer aus der AKTUELLEN Sicht, damit ein
   *  Konflikt-Nachladen die Kandidatenliste auffrischt. */
  const imDialog = dialog === null ? null
    : sicht?.quellen.find((q) => q.source === dialog.source) ?? null;
  const geaendert = dialog !== null && dialog.wahl !== dialog.vorher;

  function oeffnen(q: Eintrag): void {
    formular.zuruecksetzen();
    konflikt.current = null;
    const vorher = q.verknuepfung?.zielId ?? null;
    setDialog({ source: q.source, wahl: vorher, vorher });
  }

  /** Der EINE Ausgang neben dem Speichern (Hausregel „striktes Modalpanel"). */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(geaendert))) return;
    setDialog(null);
  }

  /**
   * Die Formularzustände des Modals (ME-Etappe 3, Aufgabe 4) — ersetzt den eigenen 409-Code:
   * Eine 409 setzt den „fremden Stand“ (die Wahl bleibt sichtbar, gesperrt), und erst
   * „Neu laden“ übernimmt die frische Sicht — samt der neuen Vergleichsbasis und dem
   * Rückfall einer verschwundenen Wahl (Schlussrunde M6). Eine 422 trägt hier Befunde zu
   * Feldern ohne eigenen Fehlerplatz (die Wahl ist eine Radiogruppe): Sie stehen ALLE in
   * der Sammelmeldung, mit Beschriftung statt technischem Namen.
   */
  const formular = useFormularZustand<Abrechnungsquellen>({
    speichern: async () => {
      if (dialog === null || dialog.wahl === null) throw new Error("Bitte eine Wahl treffen.");
      const { source, wahl } = dialog;
      try {
        // Die Version des RAHMENS, nicht die der Sicht (Fixrunde A10-2): Trägt die Sicht
        // schon eine fremde, neuere Version, während das Neuladen des Rahmens noch läuft
        // oder gescheitert ist, muss der Server mit einer echten 409 antworten — sonst
        // stempelte der ausSchreiben-Pfad die Antwortversion auf veraltete Felder.
        return await client.setzeAbrechnungsquelle(customerId, orderId, source,
          source === "MONITOR"
            ? { version, externalReferenceId: wahl }
            : { version, aircallAccountId: wahl });
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          konflikt.current = (e.conflict?.current as Abrechnungsquellen | undefined) ?? null;
        }
        throw e;
      }
    },
    neuLaden: async () => {
      const frisch = konflikt.current ?? await client.getAbrechnungsquellen(customerId, orderId);
      konflikt.current = null;
      setSicht(frisch);
      // OHNE Schreib-Herkunft: Der Rahmen sieht die fremde Version und lädt selbst neu.
      onSicht.current(frisch);
      // Die Vergleichsbasis des Modals auf die FRISCHE Verknüpfung setzen — sonst verglichen
      // „geändert" und die Abbrechen-Rückfrage gegen einen Stand, den es nicht mehr gibt.
      // Schlussrunde M6: Eine aus der frischen Sicht VERSCHWUNDENE Wahl fällt auf die frische
      // Verknüpfung zurück (bzw. auf „keine") — Speichern ist dann gesperrt, bis jemand
      // einen vorhandenen Kandidaten wählt.
      setDialog((d) => {
        if (d === null) return d;
        const eintrag = frisch.quellen.find((q) => q.source === d.source);
        const neueBasis = eintrag?.verknuepfung?.zielId ?? null;
        const nochDa = (id: string | null) =>
          id !== null && (eintrag?.kandidaten.some((k) => k.id === id) ?? false);
        return { ...d, vorher: neueBasis, wahl: nochDa(d.wahl) ? d.wahl : neueBasis };
      });
    },
    bekannteFelder: [],
    beschriftungen: {
      externalReferenceId: OBJEKT_WORT.MONITOR, aircallAccountId: OBJEKT_WORT.AIRCALL,
    },
  });
  const speichert = formular.laeuft;

  async function speichern(): Promise<void> {
    if (dialog === null || dialog.wahl === null || sicht === null) return;
    const neu = await formular.aktion();
    if (neu === undefined) return; // Befund im Modal oder fremder Stand
    setSicht(neu);
    onSicht.current(neu, { ausSchreiben: true });
    setDialog(null);
  }

  const { bestaetigen, bestaetigung } = useBestaetigung();

  /**
   * Löst die Verknüpfung einer Quelle (nur ADMIN am nicht aktiven Auftrag). Dieselbe
   * Versionsregel wie beim Speichern: die Version des RAHMENS (Fixrunde A10-2), die frische
   * Sicht mit `ausSchreiben`, bei 409 die mitgelieferte Sicht OHNE — dann lädt der Rahmen.
   */
  async function loesen(q: Eintrag): Promise<void> {
    if (q.verknuepfung === null) return;
    if (!(await bestaetigen({
      titel: "Verknüpfung lösen?",
      text: `${OBJEKT_WORT[q.source]} „${q.verknuepfung.bezeichnung}“ von diesem Auftrag `
        + "lösen? Der Auftrag ist nicht mehr aktiv — die Verknüpfung lässt sich danach "
        + "nicht wieder setzen.",
      bestaetigenText: "Lösen",
      gefaehrlich: true,
    }))) return;
    setLoest(true);
    setLoesenMeldung(null);
    try {
      const neu = await client.loeseAbrechnungsquelle(customerId, orderId, q.source, version);
      setSicht(neu);
      onSicht.current(neu, { ausSchreiben: true });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const frisch = e.conflict?.current as Abrechnungsquellen | undefined;
        if (frisch !== undefined) {
          setSicht(frisch);
          onSicht.current(frisch);
        }
        setLoesenMeldung(["Der Auftrag wurde inzwischen geändert — die Abrechnungsquellen "
          + "sind neu geladen. Bitte prüfen und erneut lösen."]);
      } else {
        const befunde = e instanceof ApiError ? (e.fields ?? []).map((f) => f.message) : [];
        setLoesenMeldung(befunde.length > 0 ? befunde
          : [e instanceof ApiError ? e.message : "Die Verknüpfung konnte nicht gelöst werden."]);
      }
    } finally {
      setLoest(false);
    }
  }

  return (
    <div className="kaq" id={`${p.idPraefix}-kasten`} tabIndex={-1}>
      {/* ÜBERGANG (Ruling Aufgabe 4): Das Lösen ist keine Maske, sondern ein Knopf an der
          Zeile — ohne Formular gibt es keinen „fremden Stand“; eine 409 übernimmt dort
          weiter sofort die mitgelieferte Sicht und sagt es (Hinweis über der Liste). */}
      {ladeFehler !== null && <Hinweis art="fehler">{ladeFehler}</Hinweis>}
      {loesenMeldung !== null && (
        <Hinweis art="fehler">
          {loesenMeldung.map((m, i) => <p key={i} className="kaq__fehlersatz">{m}</p>)}
        </Hinweis>
      )}
      {sicht === null && ladeFehler === null && <p className="kaq__hinweis">Lädt …</p>}
      {sicht !== null && sicht.quellen.length === 0 && (
        <p className="kaq__hinweis">
          Der Zahlplan dieses Auftrags rechnet nicht nach Istmengen aus Monitor oder Aircall —
          eine Verknüpfung ist nicht nötig.
        </p>
      )}
      {sicht !== null && sicht.quellen.length > 0 && (
        <ul className="kaq__liste" aria-label="Abrechnungsquellen">
          {sicht.quellen.map((q) => (
            <li key={q.source} className="kaq__zeile">
              <span className="kaq__quelle">{QUELLE_WORT[q.source]}</span>
              <Chip className="kaq__chip" statusArt={ZUSTAND_ART[q.zustand]}>
                {ZUSTAND_TEXT[q.zustand]}
              </Chip>
              <span className="kaq__ziel">
                {q.verknuepfung === null
                  ? `kein ${OBJEKT_WORT[q.source]} verknüpft` : q.verknuepfung.bezeichnung}
              </span>
              {q.verknuepfung !== null && (
                <span className="kaq__herkunft">{herkunft(q.verknuepfung)}</span>
              )}
              {aktiv && (
                <KiKnopf className="kaq__knopf"
                  name={`${QUELLE_WORT[q.source]} ${q.verknuepfung === null
                    ? "verknüpfen" : "ändern"}`}
                  deaktiviert={dialog !== null}
                  onClick={() => oeffnen(q)}>
                  {q.verknuepfung === null ? "Verknüpfen" : "Ändern"}
                </KiKnopf>
              )}
              {darfLoesen && q.verknuepfung !== null && (
                <KiKnopf className="kaq__knopf"
                  name={`${QUELLE_WORT[q.source]} lösen`}
                  deaktiviert={loest}
                  onClick={() => { void loesen(q); }}>
                  Lösen
                </KiKnopf>
              )}
            </li>
          ))}
        </ul>
      )}

      {dialog !== null && imDialog !== null && (
        <Modal titel={`${QUELLE_WORT[dialog.source]} ${dialog.vorher === null
          ? "verknüpfen" : "ändern"}`} breite="standard"
          onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
          {/* Raster und Formularzustände (me-formular): `laeuft` sperrt die Radios während
              des Speicherns (der Fokus bleibt), der fremde Stand zeigt „Neu laden“. */}
          <Formular className="kaq__dialog" beschriftung="oben" {...formular.formularProps}
            {...(formular.zustand === "fremder-stand" ? { hinweis: (
              <>
                Der Auftrag wurde inzwischen geändert. Deine Wahl steht noch da — „Neu laden“
                holt die aktuellen Abrechnungsquellen; danach die Wahl prüfen und erneut
                speichern.
              </>) } : {})}>
            {formular.meldung !== null && (
              <Hinweis art="fehler">
                {formular.meldung.split("\n").map((m, i) => (
                  <p key={i} className="kaq__fehlersatz">{m}</p>
                ))}
              </Hinweis>
            )}
            {imDialog.kandidaten.length === 0 ? (
              <p className="kaq__hinweis">
                Für diesen Kunden ist noch kein {OBJEKT_WORT[dialog.source]} hinterlegt.{" "}
                {PFLEGEORT[dialog.source]}
              </p>
            ) : (
              <fieldset className="kaq__wahl">
                <legend className="kaq__legende">
                  {OBJEKT_WORT[dialog.source]} dieses Kunden
                </legend>
                {imDialog.kandidaten.map((k) => (
                  <label key={k.id} className="kaq__option">
                    {/* Übergang P4-3 (docs/ME-DESIGN.md): natives Radio — me-design hat keine
                       Optionsgruppe (Entscheid 40). */}
                    <input type="radio" name={`${p.idPraefix}-kandidat`} value={k.id}
                      checked={dialog.wahl === k.id}
                      onChange={() => setDialog({ ...dialog, wahl: k.id })} />
                    {k.bezeichnung}
                  </label>
                ))}
              </fieldset>
            )}
          </Formular>
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit" || dialog.wahl === null
              || !geaendert}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
      {bestaetigung}
    </div>
  );
}
